import type { Dispatch, SetStateAction } from 'react';
import { Box, Button, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getSchoolsByZip, getStates, type CampusOption, type StateOption } from '../../../services/SchoolService';
import { signupSchoolFinderStyles } from './SignupSchoolFinder.styles';

export interface SignupSchoolSelection {
  zipCode: string;
  campuses: CampusOption[];
  campusId: string;
  isMissingSchool: boolean;
  stateOptions: StateOption[];
  stateCode: string;
}

export interface SignupSchoolFinderState extends SignupSchoolSelection {
  schoolLookupStatus: 'idle' | 'loading' | 'ready' | 'empty' | 'error';
  stateLookupStatus: 'idle' | 'loading' | 'ready' | 'error';
}

interface SignupSchoolFinderProps {
  value: SignupSchoolFinderState;
  onChange: Dispatch<SetStateAction<SignupSchoolFinderState>>;
  onContinue: () => void;
  actionError?: string;
  canContinue?: boolean;
  isCompleting?: boolean;
}

export function createSchoolFinderState(selection?: SignupSchoolSelection | null): SignupSchoolFinderState {
  return {
    zipCode: selection?.zipCode || '',
    campuses: selection?.campuses || [],
    campusId: selection?.campusId || '',
    isMissingSchool: selection?.isMissingSchool || false,
    stateOptions: selection?.stateOptions || [],
    stateCode: selection?.stateCode || '',
    schoolLookupStatus: selection?.campuses.length ? 'ready' : 'idle',
    stateLookupStatus: selection?.stateOptions.length ? 'ready' : 'idle',
  };
}

function formatCampusAddress(campus: CampusOption) {
  const cityAndState = [campus.city, campus.state].filter(Boolean).join(', ');
  const locality = [cityAndState, campus.zip].filter(Boolean).join(' ');
  return [campus.address, locality].filter(Boolean).join(', ');
}

export function SignupSchoolFinder({
  value,
  onChange,
  onContinue,
  actionError = '',
  canContinue = true,
  isCompleting = false,
}: SignupSchoolFinderProps) {
  const theme = useTheme();
  const {zipCode, campuses, campusId, isMissingSchool, stateOptions, stateCode, schoolLookupStatus, stateLookupStatus} = value;
  const isFindingSchools = schoolLookupStatus === 'loading';

  function updateSchoolFinder(changes: Partial<SignupSchoolFinderState>) {
    onChange((current) => ({...current, ...changes}));
  }

  function handleZipCodeChange(value: string) {
    updateSchoolFinder({zipCode: value.replace(/\D/g, '').slice(0, 5), campuses: [], campusId: '', schoolLookupStatus: 'idle'});
  }

  async function handleFindSchool() {
    updateSchoolFinder({campuses: [], campusId: '', schoolLookupStatus: 'loading'});
    try {
      const schools = await getSchoolsByZip(zipCode);
      updateSchoolFinder({campuses: schools, schoolLookupStatus: schools.length ? 'ready' : 'empty'});
    } catch {
      updateSchoolFinder({schoolLookupStatus: 'error'});
    }
  }

  async function loadStateOptions() {
    updateSchoolFinder({stateLookupStatus: 'loading'});
    try {
      const states = await getStates();
      updateSchoolFinder({stateOptions: states, stateLookupStatus: 'ready'});
    } catch {
      updateSchoolFinder({stateLookupStatus: 'error'});
    }
  }

  async function handleMissingSchool() {
    updateSchoolFinder({isMissingSchool: true, campusId: ''});
    if (stateLookupStatus === 'idle' || stateLookupStatus === 'error') {
      await loadStateOptions();
    }
  }

  function handleReturnToSchoolSearch() {
    updateSchoolFinder({isMissingSchool: false, stateCode: ''});
  }

  return (
    <>
      <Typography id="school-heading" component="h2" sx={signupSchoolFinderStyles.stepHeading()}>Find your school</Typography>
      {isMissingSchool ? (
        <Box id="missing-school-panel" sx={signupSchoolFinderStyles.missingSchoolPanel()}>
          <Typography sx={signupSchoolFinderStyles.missingSchoolMessage(theme)}>No worries. Just let us know your state and you can sign up.</Typography>
          <Box><InputLabel id="signup-state-label" sx={signupSchoolFinderStyles.fieldLabel()}>State</InputLabel><FormControl fullWidth disabled={stateLookupStatus !== 'ready' || isCompleting}><Select labelId="signup-state-label" displayEmpty value={stateCode} onChange={(event) => updateSchoolFinder({stateCode: event.target.value})} inputProps={{'aria-label': 'State'}} sx={signupSchoolFinderStyles.input()}><MenuItem value="" disabled sx={signupSchoolFinderStyles.menuItem()}>{stateLookupStatus === 'loading' ? 'Loading states…' : 'Select your state'}</MenuItem>{stateOptions.map((state) => <MenuItem key={state.code} value={state.code} sx={signupSchoolFinderStyles.menuItem()}>{state.label}</MenuItem>)}</Select></FormControl>{stateLookupStatus === 'loading' && <Typography role="status" sx={signupSchoolFinderStyles.helperText(theme)}>Loading states…</Typography>}{stateLookupStatus === 'error' && <Box sx={signupSchoolFinderStyles.stateLookupError()}><Typography role="alert" sx={signupSchoolFinderStyles.schoolLookupError(theme)}>We couldn’t load states. Check your connection and try again.</Typography><Button type="button" variant="text" disabled={isCompleting} onClick={loadStateOptions} sx={signupSchoolFinderStyles.inlineAction(theme)}>Try again</Button></Box>}</Box>
        </Box>
      ) : (
        <>
          <Box sx={signupSchoolFinderStyles.fieldGroup()}><Box sx={signupSchoolFinderStyles.zipRow()}><TextField id="signup-zip-code" label="School ZIP code" placeholder="5-digit ZIP code" value={zipCode} disabled={isFindingSchools || isCompleting} onChange={(event) => handleZipCodeChange(event.target.value)} slotProps={{inputLabel: {shrink: true}, htmlInput: {inputMode: 'numeric', pattern: '[0-9]*', maxLength: 5}}} sx={signupSchoolFinderStyles.input()} /><Button type="button" variant="contained" disableElevation disabled={zipCode.length !== 5 || isFindingSchools || isCompleting} onClick={handleFindSchool} sx={signupSchoolFinderStyles.continueButton(theme)}>{isFindingSchools ? 'Finding schools…' : 'Find schools'}</Button></Box>{schoolLookupStatus === 'loading' && <Typography role="status" sx={signupSchoolFinderStyles.helperText(theme)}>Finding schools for {zipCode}…</Typography>}{schoolLookupStatus === 'empty' && <Typography role="status" sx={signupSchoolFinderStyles.helperText(theme)}>We couldn’t find any schools for that ZIP code.</Typography>}{schoolLookupStatus === 'error' && <Typography role="alert" sx={signupSchoolFinderStyles.schoolLookupError(theme)}>We couldn’t load schools. Check your connection and try again.</Typography>}</Box>
          {campuses.length > 0 && <Box sx={signupSchoolFinderStyles.fieldGroup()}><InputLabel id="signup-campus-label" sx={signupSchoolFinderStyles.fieldLabel()}>Select your campus</InputLabel><RadioGroup aria-labelledby="signup-campus-label" aria-label="Select your campus" value={campusId} onChange={(event) => updateSchoolFinder({campusId: event.target.value})} sx={signupSchoolFinderStyles.schoolList()}>{campuses.map((campus) => <FormControlLabel key={campus.id} value={campus.id} control={<Radio disabled={isCompleting} />} label={<Box><Typography sx={signupSchoolFinderStyles.schoolName()}>{campus.name}</Typography>{formatCampusAddress(campus) && <Typography sx={signupSchoolFinderStyles.schoolAddress(theme)}>{formatCampusAddress(campus)}</Typography>}</Box>} sx={signupSchoolFinderStyles.schoolOption(theme, campusId === campus.id)} />)}</RadioGroup></Box>}
        </>
      )}
      {actionError && <Typography role="alert" sx={signupSchoolFinderStyles.completionError(theme)}>{actionError}</Typography>}
      <Box sx={signupSchoolFinderStyles.schoolActions()}><Button type="button" variant="contained" disableElevation disabled={!canContinue || isCompleting || !(isMissingSchool ? stateCode : campusId)} onClick={onContinue} sx={signupSchoolFinderStyles.continueButton(theme)}>{isCompleting ? 'Completing signup…' : 'Continue'}</Button>{isMissingSchool ? <Button type="button" variant="text" disabled={isCompleting} onClick={handleReturnToSchoolSearch} sx={signupSchoolFinderStyles.inlineAction(theme)}>Search for my school instead</Button> : <Button type="button" variant="text" disabled={isCompleting} aria-controls="missing-school-panel" onClick={() => void handleMissingSchool()} sx={signupSchoolFinderStyles.inlineAction(theme)}>Can’t find your school?</Button>}</Box>
    </>
  );
}
